import React, { useState, useRef, useEffect } from 'react';
import { Rotation } from '../Models/SkitterbookLivePagesRequest';
import './css/SkitterBookImageCropper.css';

export enum Direction {
    Horizontal = 'horizontal',
    Vertical = 'vertical',
}

export enum CropImageOrientation {
    Landscape = 'landscape',
    Portrait = 'portrait'
}

interface SkitterbookImageCropperProps {
    cropImage: string;
    cropPosX?: number;
    cropPosY?: number;
    cropWidth: number;
    cropHeight: number;
    requiredRotation?: Rotation;
    refresh?: any;
    onCropChange: (crop: { x: number; y: number }) => void;
    onCropChangeEnd?: (crop: { x: number; y: number }) => void;
}

export const SkitterBookImageCropper: React.FC<SkitterbookImageCropperProps> = ({
    cropImage,
    cropPosX = 0,
    cropPosY = 0,
    cropWidth,
    cropHeight,
    requiredRotation = "0",
    refresh,
    onCropChange,
    onCropChangeEnd
}) => {

    const [cropWindowAspectRatio, setCropWindowAspectRatio] = useState<number | null>(null);
    const [cropImageOrientation, setCropImageOrientation] = useState<CropImageOrientation>(CropImageOrientation.Landscape);
    const [offsetX, setOffsetX] = useState(cropPosX);
    const [offsetY, setOffsetY] = useState(cropPosY);
    const [movementDirection, setMovementDirection] = useState<Direction | null>(null);

    const offsetXRef = useRef(offsetX);
    const offsetYRef = useRef(offsetY);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const skitterbookImageCropRef = useRef<HTMLDivElement | null>(null);

    const maxOffsetXRef = useRef(0);
    const maxOffsetYRef = useRef(0);

    const isDraggingRef = useRef(false);
    const dragStartXRef = useRef<number | null>(null);
    const dragStartYRef = useRef<number | null>(null);
    const startXRef = useRef<number | null>(null);
    const startYRef = useRef<number | null>(null);

    useEffect(() => {
        setOffsetX(cropPosX);
        setOffsetY(cropPosY);
    }, [refresh]);

    useEffect(() => {
        offsetXRef.current = offsetX;
        offsetYRef.current = offsetY;

        onCropChange?.({
            x: offsetX,
            y: offsetY
        });
    }, [offsetX, offsetY, onCropChange]);

    useEffect(() => {
        if (containerRef.current && skitterbookImageCropRef.current) {
            maxOffsetXRef.current =
                Math.max(
                    0,
                    containerRef.current.offsetWidth -
                    skitterbookImageCropRef.current.offsetWidth
                );

            maxOffsetYRef.current =
                Math.max(
                    0,
                    containerRef.current.offsetHeight -
                    skitterbookImageCropRef.current.offsetHeight
                );
        }
    }, [movementDirection, requiredRotation]);

    useEffect(() => {

        if (!containerRef.current) {
            return;
        }

        const cropAspectRatio = cropWidth / cropHeight;

        setCropWindowAspectRatio(cropAspectRatio);

        const observer = new ResizeObserver(entries => {

            const { width, height } = entries[0].contentRect;

            if (!width || !height) {
                return;
            }

            const imageAspectRatio = width / height;

            setMovementDirection(
                cropAspectRatio >= imageAspectRatio
                    ? Direction.Vertical
                    : Direction.Horizontal
            );

            setCropImageOrientation(
                imageAspectRatio > 1
                    ? CropImageOrientation.Landscape
                    : CropImageOrientation.Portrait
            );
        });

        observer.observe(containerRef.current);

        return () => observer.disconnect();

    }, [cropWidth, cropHeight]);

    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {

        isDraggingRef.current = true;

        dragStartXRef.current = e.clientX;
        dragStartYRef.current = e.clientY;

        startXRef.current = offsetXRef.current;
        startYRef.current = offsetYRef.current;

        (e.target as HTMLElement).setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {

        if (
            !isDraggingRef.current ||
            dragStartXRef.current === null ||
            dragStartYRef.current === null ||
            startXRef.current === null ||
            startYRef.current === null
        ) {
            return;
        }

        const deltaX = e.clientX - dragStartXRef.current;
        const deltaY = e.clientY - dragStartYRef.current;

        setOffsetX(
            Math.max(
                0,
                Math.min(startXRef.current + deltaX, maxOffsetXRef.current)
            )
        );

        setOffsetY(
            Math.max(
                0,
                Math.min(startYRef.current + deltaY, maxOffsetYRef.current)
            )
        );
    };

    const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {

        isDraggingRef.current = false;

        onCropChangeEnd?.({
            x: offsetXRef.current,
            y: offsetYRef.current
        });

        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    };

    return (
        <div
            className={`skitterbook-image-cropper-container ${movementDirection} ${cropImageOrientation} req-rotation-${requiredRotation}`}
            ref={containerRef}
        >
            <img className="skitterbook-crop-image" src={`data:image/jpeg;base64,${cropImage}`} />

            <div
                className="skitterbook-image-crop"
                ref={skitterbookImageCropRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                style={{
                    "--offsetX": `${offsetX}px`,
                    "--offsetY": `${offsetY}px`,
                    "--crop-aspect-ratio": `${cropWindowAspectRatio}`,
                    "--inv-crop-aspect-ratio":
                        `${cropWindowAspectRatio ? 1 / cropWindowAspectRatio : 1}`
                } as React.CSSProperties}
            />
        </div>
    );
};
